import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { HouseholdState } from '../types';
import { loadState, resetState, saveState } from '../lib/storage';
import { seedState } from '../lib/seed';
import { backendConfigured, supabase } from '../lib/supabase';
import {
  addHouseholdMemberRemote,
  finishCookingRemote,
  loadHouseholdState,
  rebuildWeek,
  refreshPricesRemote,
  refreshShoppingRemote,
  setMealFeedbackRemote,
  startCookingRemote,
  swapMealRemote,
  toggleShoppingItemRemote,
  updateInventoryQuantityRemote,
  uploadKitchenPhoto,
  uploadReceiptPhoto,
  updateHouseholdMemberRemote,
} from '../lib/api';

type Ctx = {
  state: HouseholdState;
  setState: React.Dispatch<React.SetStateAction<HouseholdState>>;
  householdId: string | null;
  loading: boolean;
  busy: boolean;
  reset: () => void;
  reload: () => Promise<void>;
  notice: string;
  command: (text: string) => Promise<void>;
  rebuild: (instruction?: string) => Promise<void>;
  refreshShopping: () => Promise<void>;
  refreshPrices: () => Promise<void>;
  swapMeal: (id: string) => Promise<void>;
  startCooking: (id: string) => Promise<void>;
  finishCooking: (id: string) => Promise<boolean>;
  setMealFeedback: (id: string, value: 'loved'|'okay'|'skip') => Promise<void>;
  consumeInventory: (id: string) => Promise<void>;
  toggleShoppingItem: (id: string, checked: boolean) => Promise<void>;
  addMember: (name: string, role: 'adult'|'child') => Promise<void>;
  updateMember: (id: string, input: { name: string; role: 'adult'|'child'; portionFactor: number; allergies: string[]; dislikes: string[]; dietaryRules: string[] }) => Promise<void>;
  removeMember: (id: string, name: string) => Promise<boolean>;
  setKidFriendlyMode: (enabled: boolean) => Promise<void>;
  addStore: (input: { name: string; type: 'warehouse'|'asian-market'|'supermarket'|'specialty'; address?: string }) => Promise<void>;
  scanKitchen: (zone: 'fridge'|'freezer'|'pantry', file: File) => Promise<void>;
  scanReceipt: (file: File) => Promise<void>;
};

const StateContext = createContext<Ctx | null>(null);

export function StateProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState(loadState);
  const [householdId, setHouseholdId] = useState<string | null>(null);
  const [loading, setLoading] = useState(backendConfigured);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => { if (!backendConfigured) saveState(state); }, [state]);
  useEffect(() => { if (!notice) return; const t = setTimeout(() => setNotice(''), 5200); return () => clearTimeout(t); }, [notice]);

  async function reload() {
    if (!backendConfigured || !supabase) return;
    setLoading(true);
    try {
      const loaded = await loadHouseholdState();
      setHouseholdId(loaded.householdId);
      if (loaded.state) setState(loaded.state);
    } catch (error) {
      console.error(error);
      setNotice('Weekstead could not refresh the household. Please try again.');
    } finally { setLoading(false); }
  }

  useEffect(() => {
    if (!supabase) return;
    let alive = true;
    supabase.auth.getSession().then(({ data }) => { if (alive && data.session) void reload(); if (alive && !data.session) setLoading(false); });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => { if (session) void reload(); else { setHouseholdId(null); setLoading(false); } });
    return () => { alive = false; listener.subscription.unsubscribe(); };
  }, []);

  function reset() {
    if (backendConfigured) { setNotice('Production household data is protected. Use the individual controls instead of a demo reset.'); return; }
    resetState(); setState(structuredClone(seedState)); setNotice('Demo household reset.');
  }

  async function rebuild(instruction = 'Build the best household food plan for this week') {
    if (busy) { setNotice('Weekstead is already updating your plan.'); return; }
    if (!backendConfigured || !householdId) { setNotice('Production planner becomes available after your household is connected.'); return; }
    setBusy(true); setNotice('Rebuilding the household plan…');
    try { const result = await rebuildWeek(householdId, instruction); await reload(); setNotice(result.summary + (result.mode === 'deterministic' ? ' AI fallback was used for this run.' : '')); }
    catch (error) { console.error(error); setNotice(error instanceof Error ? error.message : 'The weekly plan could not be rebuilt.'); }
    finally { setBusy(false); }
  }

  async function refreshShopping() {
    if (busy) { setNotice('Weekstead is already updating your plan.'); return; }
    if (!backendConfigured || !householdId) { setNotice('Shopping refresh becomes available after your household is connected.'); return; }
    setBusy(true); setNotice('Recalculating shopping from the meals you already have…');
    try { const result = await refreshShoppingRemote(householdId); await reload(); setNotice(result.summary); }
    catch (error) { console.error(error); setNotice(error instanceof Error ? error.message : 'The shopping list could not be refreshed.'); }
    finally { setBusy(false); }
  }

  async function refreshPrices() {
    if (busy) { setNotice('Weekstead is already updating your plan.'); return; }
    if (!backendConfigured || !householdId) { setNotice('PriceCatcher+ becomes available after your household is connected.'); return; }
    setBusy(true); setNotice('PriceCatcher+ is checking current grocery prices…');
    try {
      const result = await refreshPricesRemote(householdId, 'compare');
      let reoptimized = false;
      if (result.resultCount > 0 && state.trips.length > 0) {
        try { await refreshShoppingRemote(householdId); reoptimized = true; }
        catch (error) { console.warn('PriceCatcher+ found prices but shopping could not be re-optimized yet.', error); }
      }
      await reload();
      const detail = result.stores.filter((store) => store.status === 'unsupported').length;
      const suffix = reoptimized ? ' Shopping was re-optimized with the fresh prices.' : detail ? ` ${detail} saved store${detail === 1 ? '' : 's'} do not have a live adapter yet.` : '';
      setNotice(result.summary + suffix);
    } catch (error) {
      console.error(error);
      setNotice(error instanceof Error ? error.message : 'PriceCatcher+ could not update prices.');
    } finally { setBusy(false); }
  }

  async function swapMeal(id: string) {
    if (busy) { setNotice('Weekstead is already updating your plan.'); return; }
    if (!backendConfigured || !householdId) { setNotice('Meal swapping becomes available after your household is connected.'); return; }
    setBusy(true); setNotice('Finding a better dinner and recalculating shopping…');
    try { const result = await swapMealRemote(id); await reload(); setNotice(result.summary); }
    catch (error) { console.error(error); setNotice(error instanceof Error ? error.message : 'That meal could not be swapped.'); }
    finally { setBusy(false); }
  }

  async function startCooking(id: string) {
    if (busy || !backendConfigured || !householdId) return;
    const meal = state.meals.find((m) => m.id === id);
    if (meal?.status === 'cooked' || (meal?.cookingSteps?.length && meal.status === 'cooking')) return;
    setBusy(true); setNotice('Preparing your cooking guide…');
    try { const result = await startCookingRemote(id); await reload(); setNotice(result.mode === 'ai' ? 'Cooking guide ready.' : 'Cooking mode ready.'); }
    catch (error) { console.error(error); setNotice(error instanceof Error ? error.message : 'Cooking mode could not start.'); }
    finally { setBusy(false); }
  }

  async function finishCooking(id: string) {
    if (busy || !backendConfigured || !householdId) return false;
    setBusy(true); setNotice('Finishing meal and updating the kitchen…');
    try { await finishCookingRemote(id); await reload(); setNotice('Dinner marked cooked. Tracked kitchen inventory was updated.'); return true; }
    catch (error) { console.error(error); setNotice(error instanceof Error ? error.message : 'The meal could not be finished.'); return false; }
    finally { setBusy(false); }
  }

  async function command(text: string) {
    if (backendConfigured && householdId) { await rebuild(text); return; }
    const lower = text.toLowerCase();
    if (lower.includes('not cooking') || lower.includes("aren't cooking") || lower.includes('takeout')) { setState((s) => ({ ...s, meals: s.meals.map((m, i) => i === 0 ? { ...m, name: 'Takeout / no-cook night', ingredients: [], reason: 'Planner adjusted after your change.' } : m) })); setNotice('Tonight changed to no-cook.'); return; }
    if (lower.includes('cheaper') || lower.includes('budget')) { setState((s) => ({ ...s, weeklyBudget: Math.max(100, s.weeklyBudget - 25) })); setNotice('Budget tightened.'); return; }
    setNotice('Change recorded.');
  }

  async function setMealFeedback(id: string, value: 'loved'|'okay'|'skip') {
    const previous = state; setState((s) => ({ ...s, meals: s.meals.map((m) => m.id === id ? { ...m, feedback: value } : m) }));
    if (!backendConfigured || !householdId) return;
    try { await setMealFeedbackRemote(householdId, id, value); setNotice('Meal feedback saved. Weekstead will learn from it.'); }
    catch (error) { console.error(error); setState(previous); setNotice('Meal feedback could not be saved.'); }
  }

  async function consumeInventory(id: string) {
    const item = state.inventory.find((i) => i.id === id); if (!item) return;
    const quantity = Math.max(0, Math.round((item.quantity * 0.75) * 10) / 10), previous = state;
    setState((s) => ({ ...s, inventory: s.inventory.map((i) => i.id === id ? { ...i, quantity } : i) }));
    if (!backendConfigured || !householdId) return;
    try { await updateInventoryQuantityRemote(householdId, id, quantity); } catch (error) { console.error(error); setState(previous); setNotice('Inventory change could not be saved.'); }
  }

  async function toggleShoppingItem(id: string, checked: boolean) {
    const previous = state; setState((s) => ({ ...s, trips: s.trips.map((t) => ({ ...t, items: t.items.map((i) => i.id === id ? { ...i, checked } : i) })) }));
    if (!backendConfigured || !householdId) return;
    try { await toggleShoppingItemRemote(householdId, id, checked); } catch (error) { console.error(error); setState(previous); setNotice('Shopping item could not sync.'); }
  }

  async function addMember(name: string, role: 'adult'|'child') {
    if (!backendConfigured || !householdId) return;
    try { await addHouseholdMemberRemote(householdId, { name, role }); await reload(); setNotice(`${name} added to the household.`); } catch (error) { console.error(error); setNotice('Household member could not be added.'); }
  }

  async function updateMember(id: string, input: { name: string; role: 'adult'|'child'; portionFactor: number; allergies: string[]; dislikes: string[]; dietaryRules: string[] }) {
    if (!backendConfigured || !householdId) return;
    try { await updateHouseholdMemberRemote(householdId, id, input); await reload(); setNotice(`${input.name} updated. Food rules will apply to future plans.`); } catch (error) { console.error(error); setNotice('Household member could not be updated.'); }
  }

  async function removeMember(id: string, name: string) {
    if (!backendConfigured || !householdId || !supabase) return false;
    if (state.members.length <= 1) { setNotice('A Weekstead household needs at least one household member.'); return false; }
    try {
      const result = await supabase.from('people').delete().eq('household_id', householdId).eq('id', id);
      if (result.error) throw result.error;
      await reload();
      setNotice(`${name} removed from the household.`);
      return true;
    } catch (error) {
      console.error(error); setNotice('Household member could not be removed.'); return false;
    }
  }

  async function setKidFriendlyMode(enabled: boolean) {
    if (!backendConfigured || !householdId || !supabase) { setState((s) => ({ ...s, kidFriendlyMode: enabled })); return; }
    const previous = Boolean(state.kidFriendlyMode);
    setState((s) => ({ ...s, kidFriendlyMode: enabled }));
    try {
      const result = await supabase.from('households').update({ kid_friendly_mode: enabled }).eq('id', householdId);
      if (result.error) throw result.error;
      await reload();
      setNotice(`Kid-Friendly Mode ${enabled ? 'enabled' : 'disabled'}. Future plans will follow this setting.`);
    } catch (error) {
      console.error(error); setState((s) => ({ ...s, kidFriendlyMode: previous })); setNotice('Kid-Friendly Mode could not be updated.');
    }
  }

  async function addStore(input: { name: string; type: 'warehouse'|'asian-market'|'supermarket'|'specialty'; address?: string }) {
    if (!backendConfigured || !householdId || !supabase) return;
    const cleanName = input.name.trim();
    if (!cleanName) return;
    if (state.stores.some((store) => store.name.trim().toLowerCase() === cleanName.toLowerCase())) { setNotice(`${cleanName} is already in your stores.`); return; }
    const storeType = input.type === 'asian-market' ? 'asian_market' : input.type;
    const priority = Math.min(99, Math.max(0, ...state.stores.map((store) => store.priority)) + 1);
    try {
      const result = await supabase.from('stores').insert({
        household_id: householdId,
        name: cleanName,
        store_type: storeType,
        address: input.address?.trim() || null,
        formatted_address: input.address?.trim() || null,
        cadence_days: 7,
        priority,
        provider: 'manual',
        discovered: false,
        active: true,
      });
      if (result.error) throw result.error;
      await reload();
      setNotice(`${cleanName} added. Weekstead can route specific items there even outside your normal search radius.`);
    } catch (error) { console.error(error); setNotice('Store could not be added.'); }
  }

  async function scanKitchen(zone: 'fridge'|'freezer'|'pantry', file: File) {
    if (!backendConfigured || !householdId) return;
    setNotice(`Uploading ${zone} photo…`);
    try { const result = await uploadKitchenPhoto(householdId, zone, file); await reload(); if (result.code === 'OPENAI_NOT_CONFIGURED') setNotice('Photo stored privately. AI image analysis will activate when the OpenAI secret is added.'); else setNotice(`Kitchen scan complete${result.items != null ? ` — ${result.items} items identified` : ''}.`); }
    catch (error) { console.error(error); setNotice(error instanceof Error ? error.message : 'Kitchen scan failed.'); }
  }

  async function scanReceipt(file: File) {
    if (!backendConfigured || !householdId) return;
    setNotice('Uploading grocery receipt…');
    try { const result = await uploadReceiptPhoto(householdId, file); await reload(); if (result.code === 'OPENAI_NOT_CONFIGURED') setNotice('Receipt stored privately. AI receipt extraction will activate when the OpenAI secret is added.'); else if (result.warning) setNotice(result.warning); else setNotice(`Receipt learned${result.merchantName ? ` from ${result.merchantName}` : ''}${result.items != null ? ` — ${result.items} grocery lines added` : ''}.`); }
    catch (error) { console.error(error); setNotice(error instanceof Error ? error.message : 'Receipt scan failed.'); }
  }

  const value = useMemo(() => ({ state, setState, householdId, loading, busy, reset, reload, notice, command, rebuild, refreshShopping, refreshPrices, swapMeal, startCooking, finishCooking, setMealFeedback, consumeInventory, toggleShoppingItem, addMember, updateMember, removeMember, setKidFriendlyMode, addStore, scanKitchen, scanReceipt }), [state, householdId, loading, busy, notice]);
  return <StateContext.Provider value={value}>{children}</StateContext.Provider>;
}

export function useAppState() {
  const value = useContext(StateContext);
  if (!value) throw new Error('useAppState must be used inside StateProvider');
  return value;
}
